import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, filter, of, switchMap, take, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { CompanyContextService } from '../../services/company-context.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);

  private isRefreshing = false;
  private readonly refreshedToken$ = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Requests that already carry an explicit Authorization header (e.g. the /auth/me call
    // AuthService.login() makes with the just-issued token, before that token is stored as
    // the "current" session) must not be clobbered with whatever stale token happens to be
    // in AuthService right now — that would authenticate the call as the wrong session.
    if (req.headers.has('Authorization')) {
      return next.handle(req);
    }

    if (!req.url.includes('/api/') || req.url.includes('/auth/login') || req.url.includes('/auth/refresh')) {
      return next.handle(req);
    }

    const companyScopedReq = this.addSuperAdminCompanyContext(req);
    const token = this.authService.token();
    const authReq = token ? this.addToken(companyScopedReq, token) : companyScopedReq;

    return next.handle(authReq).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401(companyScopedReq, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401(req: HttpRequest<unknown>, next: HttpHandler, originalError: HttpErrorResponse): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshedToken$.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(req, token)))
      );
    }

    this.isRefreshing = true;
    this.refreshedToken$.next(null);

    // Check whether the session is genuinely dead before attempting a refresh — an idle/revoked
    // session should log the user out locally, not churn through a doomed refresh call.
    // If the status check itself fails (e.g. endpoint unavailable), fall back to the previous
    // refresh-based behavior rather than losing the retry entirely.
    return this.authService.getSessionStatus().pipe(
      catchError(() => of(null)),
      switchMap((status) => {
        if (status && (status.isRevoked || status.idleSecondsRemaining <= 0)) {
          this.isRefreshing = false;
          this.authService.forceLogout();
          return throwError(() => originalError);
        }
        return this.refreshAndRetry(req, next);
      }),
    );
  }

  private refreshAndRetry(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.authService.refresh().pipe(
      catchError((refreshError: unknown) => {
        this.isRefreshing = false;
        this.authService.forceLogout();
        return throwError(() => refreshError);
      }),
      switchMap((session) => {
        this.isRefreshing = false;
        this.refreshedToken$.next(session.accessToken);
        return next.handle(this.addToken(req, session.accessToken));
      }),
    );
  }

  private addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private addSuperAdminCompanyContext(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const user = this.authService.session()?.user;
    if (!user?.isSuperAdmin || req.url.includes('/companies')) return req;

    const companyId = this.companyContext.getSelectedCompanyId(user.id);
    return companyId == null
      ? req
      : req.clone({ setHeaders: { 'X-Company-Id': String(companyId) } });
  }

}
